import React, { FC, useContext, useEffect, useState } from 'react'
import { uniqBy } from 'lodash'

import MainModal from './common/MainModal'

import { RiderTeamMemberModel } from '../../models/rider-team-member/riderTeamMember.model'

import { IUserExtendedWithSelected, SearchUsers } from './components/SearchUsers'
import { IRiderTeamMemberInterface } from '../../models/rider-team-member/riderTeamMember.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'

import { HandleModalContext } from '../../layout/mainlayout/MainLayout'
import UserService from '../../services/firebase-service/users/userService'
import FirestoreService from '../../services/firestoreService'

import helpers from '../../commonHelpers/helpers'
import { getConvertedData } from '../../models/interface.helper'
import { getUserFullName } from '../../helpers/helpers'
import { generatePaperworkDocument } from '../../helpers/sign'
import { getFilteredPaperwork } from '../../helpers/documents'
import useToasterHelper from '../../helpers/ToasterHelper'

import { CONST } from '../../const/const'
import { MODAL_CONSTS } from '../../const/modal-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface SwitchTeamMemberProps {
  dataToPassOn: {
    role: string
    teamMembers: IRiderTeamMemberInterface[]
    registrationByDay: IRegistrationByDayInterface
    eventReviewPublish: IEventReviewPublish
    onSave: () => Promise<void>
  }
}

export const SwitchTeamMember: FC<SwitchTeamMemberProps> = ({ dataToPassOn }) => {
  const toast = useToasterHelper()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const { role, teamMembers, registrationByDay, eventReviewPublish, onSave } = dataToPassOn

  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [defaultUsers, setDefaultUsersUsers] = useState<IUserExtendedWithSelected[]>([])
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<IUserExtendedWithSelected[]>([])

  const onChange = async (value: string) => {
    setSearchValue(value)
    try {
      setSearchLoading(true)
      const search = value.toLowerCase().replace(/\s+/g, '')
      const currentSelectedTeamMembers = selectedTeamMembers?.filter((item) => item.selected)

      if (search.length < 3) {
        setSelectedTeamMembers(uniqBy([...currentSelectedTeamMembers, ...defaultUsers], 'id'))
        return
      }

      const usersInDb = await UserService.getByName(search)
      setSelectedTeamMembers(
        uniqBy(
          [
            ...defaultUsers.filter((item) => item.selected),
            ...currentSelectedTeamMembers,
            ...usersInDb,
          ],
          'id'
        )
      )
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setSearchLoading(false)
    }
  }

  const onToggleSelect = (item: IUserExtendedWithSelected) => {
    setSelectedTeamMembers(
      selectedTeamMembers.map((user) =>
        user.id === item.id ? { ...user, selected: !user.selected } : user
      )
    )
    setDefaultUsersUsers(
      defaultUsers.map((user) =>
        user.id === item.id ? { ...user, selected: !user.selected } : user
      )
    )
  }

  const onSubmit = async () => {
    if (!registrationByDay.userId) {
      toast.error({ message: 'Registered user not found' })
      return
    }

    try {
      setLoading(true)
      const riderTeamMembersToCreate: IRiderTeamMemberInterface[] = []

      selectedTeamMembers
        .filter((user) => user.selected)
        .forEach((user) => {
          const isExist = teamMembers.find((current) => current.teamMemberId === user.id)
          const horses = registrationByDay.horsesSelected

          if (isExist) return

          horses.forEach((horse) => {
            riderTeamMembersToCreate.push(
              getConvertedData({
                ...new RiderTeamMemberModel({
                  riderDob: registrationByDay.riderDob || null,
                  riderName: registrationByDay.riderName || null,
                  riderEmail: registrationByDay.riderEmail || null,
                  riderId: registrationByDay.riderId || null,
                  riderNameNGram: registrationByDay.riderNameNGram || [],
                  riderProfilePicture: registrationByDay.riderProfilePicture || null,
                  riderCountry: registrationByDay.riderCountry || null,

                  mailLog: [],

                  teamMemberId: user.id,
                  teamMemberDob: user.userDOB,
                  teamMemberName: getUserFullName(user),
                  teamMemberRole: role,
                  teamMemberEmail: user.userEmail,
                  teamMemberProfilePicture: user.userProfilePicture,

                  userId: registrationByDay.userId || null,
                  eventId: registrationByDay.eventId || null,
                  eventName: registrationByDay.eventName || null,
                  registrationDocId: registrationByDay.registrationDocId || null,

                  horseId: horse.id || null,
                  horseName: horse.name || null,
                  horseProfilePicture: horse.picture || null,

                  modified: new Date().toString(),
                  created: new Date().toString(),
                }).toObject(),
              })
            )
          })
        })

      // Add team member
      await helpers.asyncWhileLoop({
        loopCount: riderTeamMembersToCreate.length,
        functionToFire: async (index) => {
          riderTeamMembersToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
              new RiderTeamMemberModel({
                ...riderTeamMembersToCreate[index],
              }).toFirestore()
            )
          ).id
        },
      })

      const riderTeamMembersToRemove = teamMembers.filter((item) =>
        defaultUsers.find((user) => item.teamMemberId === user.id && !user.selected)
      )

      // Remove team member
      await helpers.asyncWhileLoop({
        loopCount: riderTeamMembersToRemove?.length,
        functionToFire: async (currIndex) => {
          if (!riderTeamMembersToRemove?.[currIndex]?.id) return
          await FirestoreService.deleteItem(
            COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
            riderTeamMembersToRemove![currIndex].id!
          )
        },
      })

      const filteredPaperworkDocuments = getFilteredPaperwork(
        eventReviewPublish?.EventPaperwork ?? null
      )

      const userInDb = await UserService.getUserById(registrationByDay.userId)

      for (const document of filteredPaperworkDocuments) {
        for (const teamMember of riderTeamMembersToCreate) {
          await generatePaperworkDocument({
            paperwork: document,
            currRidersTeamMember: teamMember,
            userData: userInDb,
            eventId: teamMember.eventId || '',
            EventDetails: eventReviewPublish?.EventDetails,
            dateOfSignature: new Date(),
            isSigned: false,
          })
        }
      }

      await onSave()
      handleModal?.(false, MODAL_CONSTS.SWITCH_TEAM_MEMBER)
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const getCurrentUser = async () => {
      if (!teamMembers?.[0]) return
      try {
        setSearchLoading(true)
        const userIds = teamMembers.map((item) => item.teamMemberId || '')
        const users = await UserService.getByIds(userIds)
        const selectedUsers = users.map((user) => ({ ...user, selected: true }))
        setDefaultUsersUsers(selectedUsers)
        setSelectedTeamMembers(selectedUsers)
      } catch (error) {
        console.error('error: ', error)
      } finally {
        setSearchLoading(false)
      }
    }
    getCurrentUser().then()
  }, [teamMembers])

  return (
    <MainModal
      show={true}
      type="SWITCH_TEAM_MEMBER"
      size="md"
      title={`Switch ${role}`}
      buttons={[
        {
          label: 'Save',
          fullWidth: true,
          disabled: loading || searchLoading,
          loading: loading,
          onClick: onSubmit,
        },
        {
          label: 'Cancel',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitIconThemeColor/50',
          fullWidth: true,
          disabled: loading || searchLoading,
          onClick: () => handleModal?.(false, MODAL_CONSTS.SWITCH_TEAM_MEMBER),
        },
      ]}
    >
      <div className="text-SeabiscuitBlackThemeColor pt-4">
        <input
          type="text"
          onChange={(e) => onChange(e.target.value)}
          value={searchValue}
          placeholder="Enter at least 3 letters..."
          className="rounded-full w-full border focus:border-SeabiscuitLightThemeColorD3 border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor focus:ring-0"
        />
        <SearchUsers
          loading={searchLoading}
          searchValue={searchValue}
          users={selectedTeamMembers}
          onToggleSelect={onToggleSelect}
        />
      </div>
    </MainModal>
  )
}
